import { useCallback, useState } from "react";
import { deepSanitizeWithExclusions } from "@congrs/utils";

import { freshToken, handleUnauthorized } from "./authStore";

// Temporary solution until we have a proper API solution
const publicRoutes = [
  "/auth",
  "/registrations",
  "/public",
  // Workspace invite accept (resolve token + accept). The invitee is logged out
  // by definition, and both endpoints are skipAuth on the API side. Does not
  // match the authenticated management routes (`/workspaces/:id/invitations`).
  "/workspace-invitations",
  "/open-documents",
];

const isPublicRoute = (url) => {
  return publicRoutes.some((route) => url.includes(route));
};

// The API's `code` on an error response, read off a copy so the caller can
// still read the body.
const errorCode = async (response) => {
  try {
    return (await response.clone().json())?.code;
  } catch {
    return undefined;
  }
};

/**
 * `fetch` with the session rules: an access token about to expire is refreshed
 * first, and a 401 on a request sent with a token goes to the session, which
 * either hands back a token to retry with (once) or signs out and redirects to
 * /login. A legacy token (no refresh token) signs out on any 401, as before.
 * A refresh that can't reach the API rejects with that network error, and the
 * session is kept.
 */
async function fetchWithSession({ url, method, headers, body, token }) {
  const accessToken = await freshToken(token);

  const send = (bearer) =>
    fetch(`/api${url}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
        Authorization: `Bearer ${bearer}`,
      },
      body,
    });

  const response = await send(accessToken);
  if (response.status !== 401 || !accessToken) return response;

  const retryToken = await handleUnauthorized({
    token: accessToken,
    code: await errorCode(response),
    isRetry: false,
  });
  if (!retryToken) return response;

  const retried = await send(retryToken);
  if (retried.status === 401) {
    await handleUnauthorized({
      token: retryToken,
      code: await errorCode(retried),
      isRetry: true,
    });
  }
  return retried;
}

/**
 * Calls the API with the specified parameters.
 *
 * @param {Object} options - The options for the API call.
 * @param {string} options.url - The URL to make the API call to.
 * @param {string} options.method - The HTTP method to use for the API call.
 * @param {Object} options.body - The request body for the API call.
 * @param {string} options.token - The authorization token for the API call.
 * @param {Object} [options.headers] - Extra request headers, e.g. `AUTH_OPT_IN_HEADERS` on sign-in calls.
 * @returns {Promise<Object>} - A promise that resolves to the response data from the API.
 * @throws {Error} - If the API call fails, an error with the error message from the API response is thrown.
 */
export async function callApi({ url, method, body, token, headers }) {
  if (!token && !isPublicRoute(url)) {
    const error = new Error("No token provided");
    error.status = 401;
    throw error;
  }

  // Sanitize body while excluding structured data fields (like TipTap JSON)
  const sanitizedBody = body ? deepSanitizeWithExclusions(body) : undefined;

  const response = await fetchWithSession({
    url,
    method,
    headers,
    body: sanitizedBody ? JSON.stringify(sanitizedBody) : undefined,
    token,
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message);
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  return data;
}

/**
 * Calls the API for a binary response (e.g. a PDF). By default it auto-downloads
 * the file in the browser. Pass `download: false` to instead return the blob and
 * an object URL for the caller to use (open in a tab, print, etc.); in that case
 * the caller owns the URL and must revoke it.
 */
export async function callDownloadApi({
  url,
  method,
  body,
  token,
  headers,
  download = true,
}) {
  if (!token && !isPublicRoute(url)) {
    const error = new Error("No token provided");
    error.status = 401;
    throw error;
  }

  const sanitizedBody = body ? deepSanitizeWithExclusions(body) : undefined;

  const response = await fetchWithSession({
    url,
    method,
    headers,
    body: sanitizedBody ? JSON.stringify(sanitizedBody) : undefined,
    token,
  });

  if (!response.ok) {
    let data;
    try {
      data = await response.json();
    } catch (e) {
      throw new Error(`Download failed with status: ${response.status}`);
    }

    const error = new Error(data.message);
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition");
  let filename = "download";

  if (disposition && disposition.indexOf("filename=") !== -1) {
    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, "");
    }
  }

  const blobUrl = window.URL.createObjectURL(blob);

  if (!download) {
    return { blob, filename, url: blobUrl };
  }

  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();

  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);

  return { blob, filename };
}

// Kept because the app is literally not building without it
export function useApi() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const call = useCallback(async ({ url, method, body }) => {
    setIsLoading(true);
    setError(null);
    // Commented out for testing purposes Idea was when you change the page,
    // old data is still displayed
    // setData(null);

    const token = localStorage.getItem("token");

    try {
      const response = await callApi({ url, method, body, token });
      setData(response);
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  });

  return { data, isLoading, error, call };
}
